<template>
  <permission-gate
    moduleKey="propuestas"
    :showNoEditAlert="true"
  >
    <template #default="{ canEdit }">
      <MainBreadcrumb
        :items="items"
        showBackButton
      />
      <MainButtonsBar :align="'space-between'">
        <template #left>
          <CustomTitle>Edición de propuesta comercial</CustomTitle>
        </template>
        <template #right>
          <el-button
            v-if="proposal.Codigo > 0"
            type="primary"
            size="default"
            :icon="IconPrint"
            @click="printProposal"
            >Imprimir</el-button
          >
        </template>
      </MainButtonsBar>

      <DetailContainer>
        <div class="detail-container">
          <el-row :gutter="20">
            <CustomData
              :label="'Propuesta'"
              :spanColumn="2"
              :data="proposal?.Codigo"
            />
            <el-col :span="7">
              <div class="custom-label">Cliente/Prospecto</div>
              <el-autocomplete
                v-model="proposal.Cliente.Descripcion"
                size="default"
                :value-key="'Descripcion'"
                :fetch-suggestions="handleSearch"
                clearable
                placeholder="Ingresar el cliente"
                style="width: 100%"
                :disabled="proposal.Codigo > 0"
                @select="handleClientSelect"
              />
            </el-col>
            <CustomSelect
              v-if="hasMultipleContacts"
              v-model="selectedContacto"
              :label="'Contacto'"
              :spanColumn="4"
              :storeObject="true"
              :fieldKey="'Codigo'"
              :fieldLabel="'NombreCompleto'"
              :settings="{
                placeholder: 'Seleccionar contacto',
                options: contactOptions,
                disabled: false,
              }"
            />
            <CustomInput
              v-else
              v-model="contactoDisplay"
              :label="'Contacto'"
              :spanColumn="4"
              :settings="{
                placeholder: 'No hay contacto para mostrar',
                disabled: true,
              }"
            />
            <CustomData
              :label="'Vendedor'"
              :spanColumn="4"
              :data="`${proposal.Cliente?.UsuarioVendedorPrincipal?.Nombre || ''} ${proposal.Cliente?.UsuarioVendedorPrincipal?.Apellido || ''}`"
            />

            <CustomData
              v-if="proposal.Codigo > 0"
              :label="'Fecha creacion'"
              :spanColumn="3"
              :data="useFormatDate().formatHumanDate(proposal.FechaCreacion)"
            />
            <CustomData
              v-if="proposal.Codigo > 0"
              :label="'Fecha modificación'"
              :spanColumn="3"
              :data="
                proposal.FechaActualizacion
                  ? useFormatDate().formatHumanDate(proposal.FechaActualizacion)
                  : ''
              "
            />
          </el-row>

          <el-row :gutter="20">
            <CustomSelect
              v-model="proposal.Idioma"
              :label="'Idioma'"
              :spanColumn="3"
              :settings="{
                placeholder: 'Seleccionar idioma',
                options: ['es', 'en'],
              }"
            />
            <el-col :span="5">
              <el-row>
                <CustomInputNumber
                  v-model="proposal.Validez"
                  :label="'Validez'"
                  :spanColumn="7"
                  :settings="{
                    placeholder: 'Validez',
                    step: 1,
                    controls: false,
                  }"
                />
                <el-col :span="2">
                  <div class="label">&nbsp;</div>
                  <div style="margin-left: 5px; line-height: 30px">días</div>
                </el-col>
              </el-row>
            </el-col>
          </el-row>

          <el-row>
            <el-button
              type="primary"
              size="default"
              :icon="IconFileDocumentAddOutline"
              :disabled="!(proposal.Cliente.Codigo > 0)"
              @click="isOpenDrawerSelection = true"
              >Elegir cotizacion</el-button
            >
          </el-row>
          <el-divider />

          <div v-loading="isLoading">
            <ProposalsListItems
              v-show="!isLoading"
              ref="listItemsRef"
              :data="proposal.PropuestaCotizacionCantidad"
              @deleteItem="deleteItem"
              @approveItem="approveItem"
              @require-products="handleRequireProducts"
            />
          </div>
          <el-col :span="24">
            <div
              class="custom-label"
              style="margin: 1%"
            >
              Condiciones de venta
              <el-button
                size="default"
                :icon="IconSearch"
                circle
                @click="openModal"
              ></el-button>
            </div>
            <el-input
              v-model="proposal.Textos"
              type="textarea"
              :rows="10"
              placeholder="Condiciones de venta"
            ></el-input>
          </el-col>

          <div
            v-if="proposal.PropuestaCotizacionCantidad.length > 0 && canEdit"
            class="container"
          >
            <CustomConfirmButtons
              :disabled="false"
              :confirmDisabled="false"
              :showLoading="false"
              :confirmText="'Guardar'"
              :cancelText="'Cancelar'"
              @on-cancel="onCancel"
              @on-confirm="onConfirm"
            />
          </div>
        </div>
      </DetailContainer>

      <el-dialog
        v-model="isModalVisible"
        title="Seleccionar Condiciones de Venta"
      >
        <el-table
          :data="filteredTextOptions"
          stripe
          border
          size="default"
          @row-click="selectText"
        >
          <el-table-column
            prop="Descripcion"
            label="Descripción"
          >
            <template #default="scope">
              <div class="custom-row">{{ scope.row.Descripcion }}</div>
            </template>
          </el-table-column>
        </el-table>
      </el-dialog>

      <el-drawer
        v-if="isOpenDrawerSelection"
        v-model="isOpenDrawerSelection"
        direction="btt"
        size="65%"
      >
        <template #header>
          <div style="text-align: center">Seleccionar items para agregar a la propuesta</div>
        </template>
        <ProposalsListCotizacionesCliente />
      </el-drawer>
      <ModalAsociarProductos
        v-if="isModalAsociarProductosVisible"
        v-model="isModalAsociarProductosVisible"
        :cotizacion="cotizacionPendienteDeProductos"
        :cliente="proposal.Cliente"
        :cantidad-total-aprobada="rowAprobacionPendiente.Cantidad"
        :item-cantidad-aprobada="rowAprobacionPendiente.ItemCantidad"
        @on-success="handleProductosAsociados"
      />
    </template>
  </permission-gate>
</template>

<script setup>
import {
  onBeforeMount,
  ref,
  computed,
  watch,
  nextTick,
  provide,
} from 'vue';
import {
  useRoute,
  useRouter,
} from 'vue-router';
import { storeToRefs } from 'pinia';
import { useProposalsStore } from '../proposals_store';
import { useProspectStore } from '../../prospects/stores/prospect_store';
import { useAuthStore } from '../../auth/auth_store';
import { useTextoPredeterminadoStore } from '@/stores/texto_predeterminado.js';
import { useConfiguracionStore } from '@/stores/configuracion-general';
import MainBreadcrumb from '@/shared/layout/main/main_breadcrum.vue';
import MainButtonsBar from '@/shared/layout/main/buttons_bar/main_buttons_bar.vue';
import DetailContainer from '@/shared/layout/detail/detail_container.vue';
import CustomConfirmButtons from '@/shared/components/custom_confirm_buttons.vue';
import CustomTitle from '@/shared/components/custom_title.vue';
import CustomData from '@/shared/components/custom_data.vue';
import CustomSelect from '@/shared/components/custom_select.vue';
import CustomInput from '@/shared/components/custom_input.vue';
import CustomInputNumber from '@/shared/components/custom_input_number.vue';
import ProposalsListItems from '@/shared/components/proposals_list_items.vue';
import ProposalsListCotizacionesCliente from './components/proposals_list_cotizaciones_cliente.vue';
import { useMessageError } from '@/shared/composables/message_error.js';
import { useMessageSuccess } from '@/shared/composables/message_success.js';
import { useLoading } from '@/shared/composables/loading.js';
import { useFormatDate } from '@/shared/composables/format_date.js';
import IconFileDocumentAddOutline from '~icons/mdi/file-document-add-outline';
import IconPrint from '~icons/mdi/printer-outline';
import IconSearch from '~icons/mdi/search';
import { usePrint } from '@/shared/composables/print';
import PermissionGate from '@/shared/components/permission_gate.vue';
import ModalAsociarProductos from '@/shared/components/modal-asociar-productos.vue';
// local state
const items = [
  { label: 'Inicio', to: '/page/proposals/list', replace: false },
  { label: 'Edición', to: null, replace: false },
];
const isModalVisible = ref(false);
const route = useRoute();
const router = useRouter();
const selectedContacto = ref(null);
const listItemsRef = ref(null);
const isModalAsociarProductosVisible = ref(false);
const cotizacionPendienteDeProductos = ref(null);
const rowAprobacionPendiente = ref(null);

const proposalsStore = useProposalsStore();
const { findProposalByCodigoHTTP, saveProposal, resetProposal, approveCotizacionHTTP } =
  proposalsStore;
const { proposal, isOpenDrawerSelection, isLoading } = storeToRefs(proposalsStore);

const configuracionStore = useConfiguracionStore();
const { decimalesImportes } = storeToRefs(configuracionStore);
provide('decimalesImportes', decimalesImportes);

const textoPredeterminadoStore = useTextoPredeterminadoStore();
const { firstTextoPredeterminado } = textoPredeterminadoStore;
const { textoPredeterminadoList } = storeToRefs(textoPredeterminadoStore);

const prospectStore = useProspectStore();
const { searchProspectsHTTP } = prospectStore;

const openModal = () => {
  isModalVisible.value = true;
};
const selectText = (row) => {
  proposal.value.Textos = row.Descripcion;
  isModalVisible.value = false;
};
const filteredTextOptions = computed(() => {
  return textoPredeterminadoList.value.filter((option) => option.Idioma === proposal.value.Idioma);
});

const hasMultipleContacts = computed(() => clienteContactos.value.length > 1);
const contactOptions = computed(() => {
  return clienteContactos.value.map((c) => ({
    ...c,
    NombreCompleto:
      [c.Nombre, c.Apellido].filter(Boolean).join(' ').trim() ||
      c.Email ||
      c.TelefonoPrincipal ||
      'Sin nombre',
  }));
});

const contactoDisplay = computed(() => {
  const c = clienteContactos.value[0];
  if (!c) return '';
  const nombre = [c.Nombre, c.Apellido].filter(Boolean).join(' ').trim();
  const tel = c.TelefonoPrincipal || c.TelefonoSecundario || '';
  const email = c.Email || '';
  return nombre || email || tel || '';
});
const clienteContactos = computed(() => {
  return Array.isArray(proposal.value?.Cliente?.Contactos) ? proposal.value.Cliente.Contactos : [];
});
const onConfirm = async () => {
  try {
    useLoading().openLoading();
    proposal.value.CodigoContacto = selectedContacto.value?.Codigo || null;
    await saveProposal();
    if (proposal.value.Codigo > 0) {
      if (route.params.id === '') {
        await findProposalByCodigoHTTP(proposal.value.Codigo);
        router.replace({ path: `${route.fullPath}/${proposal.value.Codigo}` });
      }
    }
    useMessageSuccess().open(`Los datos se guardaron existosamente!`);
  } catch (err) {
    useMessageError().open(`Ha ocurrido un error al intentar guardar los datos.`);
  } finally {
    useLoading().closeLoading();
  }
};

const handleSearch = async (query, cb) => {
  const isEjecutivoCuentas = useAuthStore().findRole(1);
  let results = [];

  if (query && query.length >= 3 && query !== 'null') {
    if (isEjecutivoCuentas) {
      results = await searchProspectsHTTP(query, useAuthStore().usuario().Codigo);
    } else {
      results = await searchProspectsHTTP(query);
    }
  }
  cb(results);
};

const handleClientSelect = (item) => {
  proposal.value.Cliente = {
    ...item,
    Contactos: Array.isArray(item.Contactos) ? item.Contactos : [],
  };
  selectedContacto.value = proposal.value.Cliente.Contactos[0] || null;
};

const onCancel = () => {
  resetProposal();
  router.go(-1);
};

const printProposal = () => {
  usePrint().printReport('propuesta_comercial', {
    Codigo: proposal.value.Codigo,
  });
};

const deleteItem = ({ indexCotizacion, indexCantidad }) => {
  if (
    proposal.value.PropuestaCotizacionCantidad[indexCotizacion].items.filter((i) => i.Codigo > 0)
      .length > 1
  ) {
    proposal.value.PropuestaCotizacionCantidad[indexCotizacion].items.splice(indexCantidad, 1);
  } else {
    proposal.value.PropuestaCotizacionCantidad.splice(indexCotizacion, 1);
  }
};

const approveItem = async (payload) => {
  try {
    isLoading.value = true;
    await approveCotizacionHTTP(payload);
    useMessageSuccess().open(
      `La cotizacion ${payload.CodigoCotizacion} fue aprobada exitosamente.!`,
    );
    if (+route.params.id > 0) {
      await findProposalByCodigoHTTP(+route.params.id);
    }
  } catch (err) {
    useMessageError().open(`Ha ocurrido un error al intentar aprobar una cotizacion.`);
  } finally {
    isLoading.value = false;
  }
};

const handleRequireProducts = (row) => {
  const cotizacionAsociada = JSON.parse(JSON.stringify(row.Cotizacion));
  const productos = cotizacionAsociada.CotizacionProducto || [];

  if (productos.length === 0) {
    cotizacionAsociada.CotizacionProducto = [
      {
        Codigo: 0,
        ItemProducto: 1,
        Descripcion: cotizacionAsociada.Referencia || proposal.value.Referencia,
        Cantidad: row.Cantidad,
        CodigoProducto: null,
      },
    ];
  } else {
    productos.forEach((p) => {
      const desglose = p.CotizacionProductoCantidad?.find(
        (cpc) => cpc.ItemCantidad === row.ItemCantidad,
      );
      if (desglose && desglose.Cantidad !== null) {
        p.Cantidad = desglose.Cantidad;
      } else {
        p.Cantidad = row.Cantidad;
      }
    });
  }

  cotizacionPendienteDeProductos.value = cotizacionAsociada;
  rowAprobacionPendiente.value = row;
  isModalAsociarProductosVisible.value = true;
};

const handleProductosAsociados = async () => {
  isModalAsociarProductosVisible.value = false;

  if (+route.params.id > 0) {
    isLoading.value = true;
    await findProposalByCodigoHTTP(+route.params.id);
    isLoading.value = false;
  }

  await nextTick();

  if (rowAprobacionPendiente.value) {
    let updatedRow = null;

    const idBuscado = rowAprobacionPendiente.value.Codigo;

    for (const grupo of proposal.value.PropuestaCotizacionCantidad) {
      const found = grupo.items.find((i) => i.Codigo === idBuscado);
      if (found) {
        updatedRow = found;
        break;
      }
    }

    if (updatedRow && listItemsRef.value) {
      try {
        listItemsRef.value.continueApproval(updatedRow);
      } catch (err) {
        console.error(' Error al intentar llamar a continueApproval en el hijo:', err);
      }
    } else {
      console.warn('Falló la reanudación.', { updatedRow, listItemsRef: listItemsRef.value });
    }

    rowAprobacionPendiente.value = null;
    cotizacionPendienteDeProductos.value = null;
  }
};

watch(
  clienteContactos,
  (arr) => {
    if (arr.length) {
      if (!selectedContacto.value || !arr.some((c) => c.Codigo === selectedContacto.value.Codigo)) {
        selectedContacto.value = arr[0];
      }
    } else {
      selectedContacto.value = null;
    }
  },
  { immediate: true },
);

onBeforeMount(async () => {
  firstTextoPredeterminado();
  await configuracionStore.findAllConfiguracionesHTTP({ page: 1 });
  isLoading.value = true;
  if (+route.params.id > 0) {
    await findProposalByCodigoHTTP(+route.params.id);
  }
  isLoading.value = false;
});
</script>

<style scoped>
.detail-container {
  background-color: white;
  border: 3px solid lightgray;
  padding: 20px;
}

.custom-label {
  font-weight: 700;
  color: #111;
}
.container {
  display: flex;
  justify-content: flex-end; /* Alinea todo el contenido al final del contenedor */
}
.input-wrapper {
  display: inline-block;
  margin-right: 10px;
}
/* Marcar con borde rojo aquellos campos obligatorios que no fueron completados */

.quantity-inputs {
  display: flex; /* Esto hará que los elementos se coloquen en línea */
  flex-wrap: nowrap; /* Evita que los elementos se envuelvan a la siguiente línea */
}

.container-quantity-inputs {
  display: flex;
  overflow-x: auto;
  width: 100%;
}

/* Estilo para el botón */
.el-button-quantity {
  flex-shrink: 0; /* Evita que el botón se reduzca cuando el espacio sea limitado */
  margin: 0;
}
.custom-row {
  cursor: pointer;
  padding: 8px;
}

.custom-row:hover {
  background-color: #b9c1cc;
}
</style>
